import { Grid, Card, CardContent, Typography, Box, Button, Tooltip } from '@mui/material'
import { alpha, keyframes, useTheme } from '@mui/material/styles'
import PrintIcon from '@mui/icons-material/Print'
import { useNavigate } from 'react-router-dom'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import SavingsIcon from '@mui/icons-material/Savings'
import Co2Icon from '@mui/icons-material/Co2'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import SpeedIcon from '@mui/icons-material/Speed'
import EfficiencyDistribution from '../components/charts/EfficiencyDistribution'
import EquipmentBreakdown from '../components/charts/EquipmentBreakdown'
import IndustryComparison from '../components/charts/IndustryComparison'
import AgePerformance from '../components/charts/AgePerformance'
import KwTonComparison from '../components/charts/KwTonComparison'
import RefrigerantAnalysis from '../components/charts/RefrigerantAnalysis'
import EfficiencyTrend from '../components/charts/EfficiencyTrend'
import InstallationsMap from '../components/maps/InstallationsMap'
import FilterPanel from '../components/FilterPanel'
import { useFilters } from '../context/FilterContext'
import { InteractiveChartWrapper } from '../components/ui/ChartClickHint'
import QuickROICalculator from '../components/ui/QuickROICalculator'
import { QuickFindWidget } from '../components/ui/GuidedWorkflow'
import StatCard from '../components/ui/StatCard'
import { ContextCard, ComparisonCard, TrendCard, KPICard } from '../components/ui/cards'
import { EFFICIENCY_STATS, FINANCIAL_STATS } from '../services/corpusStatistics'
import DataQualityInfo from '../components/ui/DataQualityModal'
import stats from '../data/stats.json'
import { tokens } from '../theme'

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

// Section header component with accent underline
function SectionHeader({ children, delay = 0 }) {
  const theme = useTheme()
  const palette = theme.palette
  return (
    <Box
      sx={{
        position: 'relative',
        mb: 3,
        animation: `${fadeInUp} 0.6s ease-out forwards`,
        animationDelay: `${delay}s`,
        opacity: 0
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontWeight: 700,
          color: 'text.primary',
          letterSpacing: '-0.01em',
          display: 'inline-block',
          pb: 1,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '60px',
            height: '3px',
            background: `linear-gradient(90deg, ${palette.primary.main} 0%, ${palette.accent?.teal || '#4ECDC4'} 100%)`,
            borderRadius: 0.5
          }
        }}
      >
        {children}
      </Typography>
    </Box>
  )
}

// Glassmorphism chart card
function ChartCard({ title, height = 350, children, delay = 0 }) {
  const theme = useTheme()
  const palette = theme.palette
  return (
    <Card
      sx={{
        height,
        background: palette.background.paper,
        border: `1px solid ${palette.divider}`,
        transition: `all ${tokens.transitions.normal}`,
        animation: `${fadeInUp} 0.6s ease-out forwards`,
        animationDelay: `${delay}s`,
        opacity: 0,
        overflow: 'hidden',
        '&:hover': {
          borderColor: alpha(palette.primary.main, 0.2),
          transform: 'translateY(-2px)',
          boxShadow: `0 4px 16px rgba(0,0,0,0.15), 0 0 20px ${alpha(palette.primary.main, 0.1)}`
        },
        // Top accent line
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: `linear-gradient(90deg, ${palette.primary.main} 0%, transparent 100%)`,
          opacity: 0.6
        }
      }}
    >
      <CardContent sx={{ height: '100%', p: { xs: 2, md: 3 } }}>
        <Typography
          variant="h6"
          sx={{
            fontFamily: '"Outfit", sans-serif',
            fontWeight: 600,
            color: 'text.primary',
            mb: 2,
            fontSize: { xs: '0.95rem', sm: '1.1rem' }
          }}
        >
          {title}
        </Typography>
        <Box sx={{ height: 'calc(100% - 40px)' }}>
          {children}
        </Box>
      </CardContent>
    </Card>
  )
}

function Dashboard() {
  const navigate = useNavigate()
  const { filteredData, filteredStats, activeFilterCount } = useFilters()
  const theme = useTheme()
  const palette = theme.palette

  // Handle chart bar clicks - navigate to Data Explorer with filter
  const handleChartClick = (filterType, filterValue) => {
    const params = new URLSearchParams()
    params.set(filterType, filterValue)
    navigate(`/data-explorer?${params.toString()}`)
  }

  // Use filtered stats when filters are active, otherwise use global stats
  const displayStats = activeFilterCount > 0 ? {
    mean_efficiency: filteredStats.avgEfficiency.toFixed(1),
    total_cost_savings: filteredStats.totalSavings,
    measurement_count: filteredStats.count,
    kw_ton_before: filteredStats.avgKwTonBefore.toFixed(3),
    kw_ton_after: filteredStats.avgKwTonAfter.toFixed(3)
  } : {
    mean_efficiency: stats.mean_efficiency,
    total_cost_savings: stats.total_cost_savings,
    measurement_count: stats.total_measurements,
    kw_ton_before: '1.247',
    kw_ton_after: '0.896'
  }

  // Calculate kW/Ton reduction percentage
  const kwTonReduction = displayStats.kw_ton_before > 0
    ? ((parseFloat(displayStats.kw_ton_before) - parseFloat(displayStats.kw_ton_after)) / parseFloat(displayStats.kw_ton_before) * 100).toFixed(1)
    : 0

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
      {/* Page Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 4,
          flexWrap: 'wrap',
          gap: 2,
          animation: `${fadeInUp} 0.6s ease-out forwards`
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="h3"
            sx={{
              fontFamily: '"Outfit", sans-serif',
              fontWeight: 800,
              fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' },
              background: `linear-gradient(135deg, ${palette.text.primary} 0%, ${palette.primary.main} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '-0.02em'
            }}
          >
            Performance Dashboard
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              mt: 0.5,
              fontWeight: 500
            }}
          >
            PermaFrost NMR Lab and Engineering Corpus
          </Typography>
        </Box>
        <Tooltip title="Print dashboard" arrow>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
            aria-label="Print dashboard"
            sx={{
              flexShrink: 0,
              borderColor: alpha(palette.text.primary, 0.15),
              color: 'text.secondary',
              '&:hover': {
                borderColor: palette.primary.main,
                color: palette.primary.main,
                bgcolor: alpha(palette.primary.main, 0.1)
              }
            }}
          >
            Print
          </Button>
        </Tooltip>
      </Box>

      {/* Filter Panel */}
      <Box
        sx={{
          mb: 4,
          animation: `${fadeInUp} 0.6s ease-out forwards`,
          animationDelay: '0.1s',
          opacity: 0
        }}
      >
        <FilterPanel compact />
      </Box>

      {/* Stats Cards — each card type matches its data shape */}
      <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 5 }}>
        {/* Efficiency Gain — ContextCard with benchmark scale */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Box sx={{ animation: `${fadeInUp} 0.5s ease-out forwards`, animationDelay: '0.1s', opacity: 0 }}>
            <ContextCard
              title="Avg Efficiency Gain"
              value={activeFilterCount > 0 ? parseFloat(displayStats.mean_efficiency) : EFFICIENCY_STATS.mean}
              displayValue={activeFilterCount > 0 ? `${displayStats.mean_efficiency}%` : `${EFFICIENCY_STATS.mean}%`}
              uncertainty={activeFilterCount > 0 ? null : `± ${EFFICIENCY_STATS.stdDev}%`}
              benchmarkMin={0}
              benchmarkMax={60}
              benchmarkValue={15}
              benchmarkLabel="Industry avg"
              unit="%"
              subtitle={activeFilterCount > 0
                ? `${displayStats.measurement_count} filtered studies`
                : `n=${EFFICIENCY_STATS.n}, 95% CI: ${EFFICIENCY_STATS.ci95.lower}-${EFFICIENCY_STATS.ci95.upper}%`}
              icon={<TrendingUpIcon />}
              color="#A0CE4E"
            />
          </Box>
        </Grid>

        {/* kW/Ton Reduction — ComparisonCard with before/after */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Box sx={{ animation: `${fadeInUp} 0.5s ease-out forwards`, animationDelay: '0.15s', opacity: 0 }}>
            <ComparisonCard
              title="kW/Ton Reduction"
              beforeValue={displayStats.kw_ton_before}
              afterValue={displayStats.kw_ton_after}
              changeValue={`${kwTonReduction}%`}
              changeLabel="reduction"
              subtitle="Lower kW/ton = more efficient cooling"
              icon={<SpeedIcon />}
              color="#4ECDC4"
            />
          </Box>
        </Grid>

        {/* Savings per Ton — TrendCard with sparkline */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Box sx={{ animation: `${fadeInUp} 0.5s ease-out forwards`, animationDelay: '0.2s', opacity: 0 }}>
            <TrendCard
              title="Savings per Ton"
              value={`$${FINANCIAL_STATS.savingsPerTon.value}/yr`}
              trendData={[65, 78, 85, 92, 105, 112, 120]}
              trendLabel="Consistent across studies"
              subtitle={`Per ton capacity, n=${FINANCIAL_STATS.savingsPerTon.n}`}
              icon={<SavingsIcon />}
              color="#A0CE4E"
            />
          </Box>
        </Grid>

        {/* CO2 Avoided — StatCard (cumulative value, no special shape) */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Box sx={{ animation: `${fadeInUp} 0.5s ease-out forwards`, animationDelay: '0.25s', opacity: 0 }}>
            <StatCard
              title="CO2 Avoided"
              value={`${(stats.co2_avoided / 1000).toFixed(1)}K`}
              subtitle="Metric tonnes (EPA grid factor)"
              icon={<Co2Icon />}
              color="#4CAF50"
            />
          </Box>
        </Grid>

        {/* Study Coverage — KPICard with radial progress */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Box sx={{ animation: `${fadeInUp} 0.5s ease-out forwards`, animationDelay: '0.3s', opacity: 0 }}>
            <KPICard
              title="Study Coverage"
              value={stats.us_states}
              total={50}
              label={`${stats.us_states} States`}
              subtitle={`${stats.countries} countries, ${stats.total_locations} sites`}
              icon={<LocationOnIcon />}
              color="#63B3ED"
            />
          </Box>
        </Grid>
      </Grid>

      {/* Quick Tools Row */}
      <Box sx={{ mb: 5 }}>
        <SectionHeader delay={0.2}>Quick Tools</SectionHeader>
        <Grid container spacing={2}>
          <Grid item xs={12} md={9}>
            <Box
              sx={{
                animation: `${fadeInUp} 0.6s ease-out forwards`,
                animationDelay: '0.25s',
                opacity: 0
              }}
            >
              <QuickROICalculator compact />
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box
              sx={{
                animation: `${fadeInUp} 0.6s ease-out forwards`,
                animationDelay: '0.3s',
                opacity: 0
              }}
            >
              <QuickFindWidget />
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Section: kW/Ton Analysis */}
      <Box sx={{ mb: 5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <SectionHeader delay={0.3}>Energy Performance Analysis</SectionHeader>
          <Box
            sx={{
              animation: `${fadeInUp} 0.6s ease-out forwards`,
              animationDelay: '0.35s',
              opacity: 0
            }}
          >
            <DataQualityInfo />
          </Box>
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ChartCard title="kW/Ton Comparison" height={380} delay={0.35}>
              <KwTonComparison data={filteredData} />
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartCard title="Performance by Refrigerant" height={380} delay={0.4}>
              <RefrigerantAnalysis data={filteredData} onBarClick={handleChartClick} />
            </ChartCard>
          </Grid>
        </Grid>
      </Box>

      {/* Section: Geographic & Distribution */}
      <Box sx={{ mb: 5 }}>
        <SectionHeader delay={0.4}>Geographic Coverage & Distribution</SectionHeader>
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <ChartCard title="Testing Locations" height={420} delay={0.45}>
              <InstallationsMap />
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={5}>
            <ChartCard title="Efficiency Improvement Distribution" height={420} delay={0.5}>
              <EfficiencyDistribution data={filteredData} onBarClick={handleChartClick} />
            </ChartCard>
          </Grid>
        </Grid>
      </Box>

      {/* Section: Equipment & Industry Performance */}
      <Box sx={{ mb: 5 }}>
        <SectionHeader delay={0.5}>Equipment & Industry Performance</SectionHeader>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ChartCard title="Performance by Equipment Type" height={380} delay={0.55}>
              <EquipmentBreakdown data={filteredData} onBarClick={handleChartClick} />
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartCard title="Performance by Industry" height={380} delay={0.6}>
              <IndustryComparison data={filteredData} onBarClick={handleChartClick} />
            </ChartCard>
          </Grid>
        </Grid>
      </Box>

      {/* Section: Age & Trend Analysis */}
      <Box sx={{ mb: 4 }}>
        <SectionHeader delay={0.6}>Age & Trend Analysis</SectionHeader>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ChartCard title="Efficiency Improvement by Equipment Age" height={380} delay={0.65}>
              <AgePerformance data={filteredData} onPointClick={handleChartClick} />
            </ChartCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                animation: `${fadeInUp} 0.6s ease-out forwards`,
                animationDelay: '0.7s',
                opacity: 0
              }}
            >
              <EfficiencyTrend data={filteredData} title="Efficiency Improvement Over Time" />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}

export default Dashboard
